import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import { useTableSort } from '../../../../hooks/useTableSort';
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader';

// Types
interface Fund {
  BackstopID: number;
  FundName: string;
  Strategy?: string;
  Style?: string;
  Currency?: string;
  Invested?: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

interface FundClass {
  ClassID: number;
  BackstopID: number;
  ClassName: string;
  Currency: string;
  IsDefault: boolean;
  Timestamp?: string;
  UserName?: string;
  CreatedDate?: string;
  ModifiedDate?: string;
}

interface Strategy {
  StrategyID: number;
  StrategyName: string;
}

interface Style {
  StyleID: number;
  StyleName: string;
}

const FundManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  const { 
    funds: allFunds, 
    fundClasses, 
    strategies, 
    styles,
    loading: contextLoading,
    addFund,
    updateFund,
    removeFund,
    addFundClass,
    updateFundClass,
    removeFundClass,
    refreshFunds,
    refreshFundClasses
  } = useHedgeFundData();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    BackstopID: '',
    FundName: '',
    StrategyID: '',
    StyleID: '',
    Invested: true
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    BackstopID: '',
    FundName: '',
    StrategyID: '',
    StyleID: '',
    Invested: true
  });
  
  // Fund Class form state
  const [classFormData, setClassFormData] = useState<{
    BackstopID: string;
    ClassName: string;
    Currency: string;
    IsDefault: boolean;
  }>({
    BackstopID: '',
    ClassName: '',
    Currency: '',
    IsDefault: true
  });
  const [editingClassId, setEditingClassId] = useState<number | null>(null);
  const [appliedClassFilters, setAppliedClassFilters] = useState<{
    BackstopID: string;
    ClassName: string;
    Currency: string;
    IsDefault: string | boolean;
  }>({
    BackstopID: '',
    ClassName: '',
    Currency: '',
    IsDefault: ''
  });
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [classPage, setClassPage] = useState(0);
  const [classRowsPerPage, setClassRowsPerPage] = useState(5);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fundToDelete, setFundToDelete] = useState<Fund | null>(null);
  const [deleteClassDialogOpen, setDeleteClassDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<FundClass | null>(null);

  // Get unique currencies from fund classes data
  const getUsedCurrencies = () => {
    const usedCurrencies = [...new Set(fundClasses.map(fc => fc.Currency).filter(Boolean))];
    const standardCurrencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'];
    
    // Combine used currencies with standard ones, removing duplicates
    const allCurrencies = [...new Set([...usedCurrencies, ...standardCurrencies])];
    return allCurrencies.sort();
  };

  // Use funds from context
  const funds = allFunds;

  // Handle form changes
  const handleInputChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle class form changes
  const handleClassInputChange = (field: keyof typeof classFormData, value: string | boolean) => {
    setClassFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const url = editingId 
        ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/funds/${editingId}`
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/funds`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          BackstopID: parseInt(formData.BackstopID),
          FundName: formData.FundName,
          StrategyID: parseInt(formData.StrategyID),
          StyleID: parseInt(formData.StyleID),
          Invested: formData.Invested
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingId ? 'update' : 'create'} fund`);
      }
      
      setSuccess(`Fund ${editingId ? 'updated' : 'created'} successfully`);
      handleClear();
      refreshFunds(); // Reload the funds list
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (fund: Fund) => {
    // Find the strategy ID by matching the strategy name
    const strategyId = strategies.find(s => s.StrategyName === fund.Strategy)?.StrategyID?.toString() || '';
    
    // Find the style ID by matching the style name
    const styleId = styles.find(s => s.StyleName === fund.Style)?.StyleID?.toString() || '';
    
    setFormData({
      BackstopID: fund.BackstopID.toString(),
      FundName: fund.FundName,
      StrategyID: strategyId,
      StyleID: styleId,
      Invested: fund.Invested || true
    });
    setEditingId(fund.BackstopID);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!fundToDelete) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/funds/${fundToDelete.BackstopID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to delete fund');
      }
      
      setSuccess('Fund deleted successfully');
      setDeleteDialogOpen(false);
      setFundToDelete(null);
      refreshFunds(); // Reload the funds list
      
    } catch (err) {
      setDeleteDialogOpen(false);
      setFundToDelete(null);
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    setAppliedFilters({ ...formData });
    setPage(0); // Reset pagination when filtering
  };

  // Handle clear form
  const handleClear = () => {
    setFormData({
      BackstopID: '',
      FundName: '',
      StrategyID: '',
      StyleID: '',
      Invested: true
    });
    setAppliedFilters({
      BackstopID: '',
      FundName: '',
      StrategyID: '',
      StyleID: '',
      Invested: true
    });
    setEditingId(null);
  };

  // Check if any filter criteria are set in form
  const hasFilterCriteria = formData.BackstopID || formData.FundName || 
    formData.StrategyID || formData.StyleID;

  // Check if any filters are currently applied
  const hasAppliedFilters = appliedFilters.BackstopID || appliedFilters.FundName || 
    appliedFilters.StrategyID || appliedFilters.StyleID;

  // Filter funds
  const filteredFunds = funds.filter(fund => {
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedFilters || (
      (!appliedFilters.BackstopID || fund.BackstopID.toString().includes(appliedFilters.BackstopID)) &&
      (!appliedFilters.FundName || fund.FundName.toLowerCase().includes(appliedFilters.FundName.toLowerCase())) &&
      (!appliedFilters.StrategyID || fund.Strategy === strategies.find(s => s.StrategyID.toString() === appliedFilters.StrategyID)?.StrategyName) &&
      (!appliedFilters.StyleID || fund.Style === styles.find(s => s.StyleID.toString() === appliedFilters.StyleID)?.StyleName)
    );
    
    return matchesFormFilters;
  });

  // Apply sorting to filtered funds
  const { sortedData: sortedFunds, handleSort: handleFundSort, getSortDirection: getFundSortDirection, getSortIndex: getFundSortIndex } = useTableSort({
    data: filteredFunds,
    initialSort: [{ field: 'BackstopID', direction: 'asc' }] // Default sort by BackstopID ascending
  });

  // Handle class form submission
  const handleClassSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const url = editingClassId 
        ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-classes/${editingClassId}`
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-classes`;
      
      const method = editingClassId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          BackstopID: parseInt(classFormData.BackstopID),
          ClassName: classFormData.ClassName,
          Currency: classFormData.Currency,
          IsDefault: classFormData.IsDefault
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingClassId ? 'update' : 'create'} fund class`);
      }
      
      setSuccess(`Fund class ${editingClassId ? 'updated' : 'created'} successfully`);
      handleClassClear();
      refreshFundClasses(); // Reload the fund classes list
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Class operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle class search button click
  const handleClassSearch = () => {
    setAppliedClassFilters({ ...classFormData });
    setClassPage(0); // Reset pagination when filtering
  };

  // Handle class clear form
  const handleClassClear = () => {
    setClassFormData({
      BackstopID: '',
      ClassName: '',
      Currency: '',
      IsDefault: true
    });
    setAppliedClassFilters({
      BackstopID: '',
      ClassName: '',
      Currency: '',
      IsDefault: ''
    });
    setEditingClassId(null);
  };

  // Check if any class filter criteria are set in form
  const hasClassFilterCriteria = classFormData.BackstopID || classFormData.ClassName || 
    (classFormData.Currency !== 'USD');

  // Check if any class filters are currently applied
  const hasAppliedClassFilters = appliedClassFilters.BackstopID || appliedClassFilters.ClassName || 
    appliedClassFilters.Currency || appliedClassFilters.IsDefault;

  // Filter fund classes
  const filteredFundClasses = fundClasses.filter(fundClass => {
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedClassFilters || (
      (!appliedClassFilters.BackstopID || fundClass.BackstopID.toString() === appliedClassFilters.BackstopID) &&
      (!appliedClassFilters.ClassName || fundClass.ClassName.toLowerCase().includes(appliedClassFilters.ClassName.toLowerCase())) &&
      (!appliedClassFilters.Currency || fundClass.Currency === appliedClassFilters.Currency) &&
      (!appliedClassFilters.IsDefault || fundClass.IsDefault.toString() === appliedClassFilters.IsDefault.toString())
    );
    
    return matchesFormFilters;
  });

  // Apply sorting to filtered fund classes
  const { sortedData: sortedFundClasses, handleSort: handleClassSort, getSortDirection: getClassSortDirection, getSortIndex: getClassSortIndex } = useTableSort({
    data: filteredFundClasses,
    initialSort: [{ field: 'BackstopID', direction: 'asc' }] // Default sort by BackstopID ascending
  });

  // Handle class edit
  const handleClassEdit = (fundClass: FundClass) => {
    setClassFormData({
      BackstopID: fundClass.BackstopID.toString(),
      ClassName: fundClass.ClassName,
      Currency: fundClass.Currency,
      IsDefault: fundClass.IsDefault
    });
    setEditingClassId(fundClass.ClassID);
  };

  // Handle class delete
  const handleClassDelete = async () => {
    if (!classToDelete) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-classes/${classToDelete.ClassID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete fund class');
      }
      
      setSuccess('Fund class deleted successfully');
      setDeleteClassDialogOpen(false);
      setClassToDelete(null);
      refreshFundClasses(); // Reload the fund classes list
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Class delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Paginated funds (use sorted data)
  const paginatedFunds = sortedFunds.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Paginated fund classes (use sorted data)
  const paginatedFundClasses = sortedFundClasses.slice(
    classPage * classRowsPerPage,
    classPage * classRowsPerPage + classRowsPerPage
  );

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Fund Management
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Fund Management Section */}
      <Grid container spacing={3}>
        {/* Fund Form */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} data-form-section>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {editingId ? 'Edit Fund' : hasFilterCriteria ? 'Add New Fund / Search Filters' : 'Add New Fund'}
            </Typography>
            {hasFilterCriteria && !editingId && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="body2">
                  Form fields are being used to filter the funds list. Click "Search" to apply filters or "Clear" to reset.
                </Typography>
              </Alert>
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Backstop ID *"
                  type="number"
                  value={formData.BackstopID}
                  onChange={(e) => handleInputChange('BackstopID', e.target.value)}
                  fullWidth
                  required
                  disabled={!!editingId} // Disable when editing
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fund Name *"
                  value={formData.FundName}
                  onChange={(e) => handleInputChange('FundName', e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Strategy</InputLabel>
                  <Select
                    value={formData.StrategyID}
                    onChange={(e) => handleInputChange('StrategyID', e.target.value)}
                    label="Strategy"
                  >
                    {strategies.map((strategy) => (
                      <MenuItem key={`strategy-${strategy.StrategyID}`} value={strategy.StrategyID.toString()}>
                        {strategy.StrategyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Style</InputLabel>
                  <Select
                    value={formData.StyleID}
                    onChange={(e) => handleInputChange('StyleID', e.target.value)}
                    label="Style"
                  >
                    {styles.map((style) => (
                      <MenuItem key={`style-${style.StyleID}`} value={style.StyleID.toString()}>
                        {style.StyleName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    id="invested-checkbox"
                    checked={formData.Invested}
                    onChange={(e) => handleInputChange('Invested', e.target.checked)}
                  />
                  <label htmlFor="invested-checkbox">
                    <Typography variant="body2">Invested (default: true)</Typography>
                  </label>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading || !formData.BackstopID || !formData.FundName}
                  sx={{ flex: hasFilterCriteria && !editingId ? 1 : 2 }}
                >
                  {loading ? 'Processing...' : (editingId ? 'Update' : 'Create')}
                </Button>
                {hasFilterCriteria && !editingId && (
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    Search
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Funds Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="h6">
                  Funds List ({filteredFunds.length} of {funds.length})
                </Typography>
                {hasAppliedFilters && (
                  <Typography variant="body2" color="text.secondary">
                    Filtered by form criteria
                  </Typography>
                )}
              </Box>
              {/* Refresh functionality now handled by global refresh button in tabs */}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableHeader
                      field="BackstopID"
                      label="Backstop ID"
                      sortDirection={getFundSortDirection('BackstopID')}
                      sortIndex={getFundSortIndex('BackstopID')}
                      onSort={handleFundSort}
                    />
                    <SortableTableHeader
                      field="FundName"
                      label="Fund Name"
                      sortDirection={getFundSortDirection('FundName')}
                      sortIndex={getFundSortIndex('FundName')}
                      onSort={handleFundSort}
                    />
                    <SortableTableHeader
                      field="Strategy"
                      label="Strategy"
                      sortDirection={getFundSortDirection('Strategy')}
                      sortIndex={getFundSortIndex('Strategy')}
                      onSort={handleFundSort}
                    />
                    <SortableTableHeader
                      field="Style"
                      label="Style"
                      sortDirection={getFundSortDirection('Style')}
                      sortIndex={getFundSortIndex('Style')}
                      onSort={handleFundSort}
                    />
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedFunds.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No funds found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedFunds.map((fund) => (
                      <TableRow key={fund.BackstopID}>
                        <TableCell>{fund.BackstopID}</TableCell>
                        <TableCell>{fund.FundName}</TableCell>
                        <TableCell>
                          {fund.Strategy && (
                            <Chip label={fund.Strategy} size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {fund.Style && (
                            <Chip label={fund.Style} size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(fund)}
                                disabled={loading}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setFundToDelete(fund);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={loading}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredFunds.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

        {/* Fund Class Management Section */}
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {/* Fund Class Form */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }} data-form-section>
              <Typography variant="h6" sx={{ mb: 1.5, color: '#1a237e' }}>
                {editingClassId ? 'Edit Fund Class' : hasClassFilterCriteria ? 'Add Fund Class / Search Filters' : 'Add New Fund Class'}
              </Typography>
              {hasClassFilterCriteria && !editingClassId && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    Form fields are being used to filter the fund classes list. Click "Search" to apply filters or "Clear" to reset.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Backstop ID *"
                    type="number"
                    value={classFormData.BackstopID}
                    onChange={(e) => handleClassInputChange('BackstopID', e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Fund</InputLabel>
                    <Select
                      value={classFormData.BackstopID}
                      onChange={(e) => handleClassInputChange('BackstopID', e.target.value)}
                      label="Select Fund"
                    >
                      {funds.map((fund) => (
                        <MenuItem key={`fundclass-${fund.BackstopID}`} value={fund.BackstopID.toString()}>
                          {fund.FundName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Class Name *"
                    value={classFormData.ClassName}
                    onChange={(e) => handleClassInputChange('ClassName', e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    freeSolo
                    options={getUsedCurrencies()}
                    value={classFormData.Currency}
                    onChange={(event, newValue) => {
                      handleClassInputChange('Currency', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      handleClassInputChange('Currency', newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Currency"
                        placeholder="Any Currency"
                        helperText="Select from list or type new currency"
                        size="small"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={`currency-${option}`}>
                        {option || 'Any Currency'}
                      </li>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id="default-class-checkbox"
                      checked={classFormData.IsDefault}
                      onChange={(e) => handleClassInputChange('IsDefault', e.target.checked)}
                    />
                    <label htmlFor="default-class-checkbox">
                      <Typography variant="body2">This is the default class</Typography>
                    </label>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleClassSubmit}
                    disabled={loading || !classFormData.BackstopID || !classFormData.ClassName}
                    sx={{ flex: hasClassFilterCriteria && !editingClassId ? 1 : 2 }}
                    color="secondary"
                  >
                    {loading ? 'Processing...' : (editingClassId ? 'Update Class' : 'Save Class')}
                  </Button>
                  {hasClassFilterCriteria && !editingClassId && (
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleClassSearch}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      Search
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClassClear}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Fund Classes Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: '#1a237e' }}>
                    Fund Classes ({filteredFundClasses.length} of {fundClasses.length})
                  </Typography>
                  {hasAppliedClassFilters && (
                    <Typography variant="body2" color="text.secondary">
                      Filtered by form criteria
                    </Typography>
                  )}
                </Box>
                {/* Refresh functionality now handled by global refresh button in tabs */}
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableHeader
                        field="BackstopID"
                        label="Backstop ID"
                        sortDirection={getClassSortDirection('BackstopID')}
                        sortIndex={getClassSortIndex('BackstopID')}
                        onSort={handleClassSort}
                      />
                      <SortableTableHeader
                        field="FundName"
                        label="Fund Name"
                        sortDirection={getClassSortDirection('FundName')}
                        sortIndex={getClassSortIndex('FundName')}
                        onSort={handleClassSort}
                      />
                      <SortableTableHeader
                        field="ClassName"
                        label="Class Name"
                        sortDirection={getClassSortDirection('ClassName')}
                        sortIndex={getClassSortIndex('ClassName')}
                        onSort={handleClassSort}
                      />
                      <SortableTableHeader
                        field="Currency"
                        label="Currency"
                        sortDirection={getClassSortDirection('Currency')}
                        sortIndex={getClassSortIndex('Currency')}
                        onSort={handleClassSort}
                      />
                      <SortableTableHeader
                        field="IsDefault"
                        label="Default?"
                        sortDirection={getClassSortDirection('IsDefault')}
                        sortIndex={getClassSortIndex('IsDefault')}
                        onSort={handleClassSort}
                      />
                      <SortableTableHeader
                        field="Timestamp"
                        label="Last Modified"
                        sortDirection={getClassSortDirection('Timestamp')}
                        sortIndex={getClassSortIndex('Timestamp')}
                        onSort={handleClassSort}
                      />
                      <SortableTableHeader
                        field="UserName"
                        label="Created By"
                        sortDirection={getClassSortDirection('UserName')}
                        sortIndex={getClassSortIndex('UserName')}
                        onSort={handleClassSort}
                      />
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedFundClasses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No fund classes found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedFundClasses.map((fundClass) => {
                        const fund = funds.find(f => f.BackstopID === fundClass.BackstopID);
                        return (
                          <TableRow 
                            key={fundClass.ClassID}
                            onDoubleClick={() => handleClassEdit(fundClass)}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                          >
                            <TableCell>{fundClass.BackstopID}</TableCell>
                            <TableCell>{fund?.FundName || `Fund ${fundClass.BackstopID}`}</TableCell>
                            <TableCell>{fundClass.ClassName}</TableCell>
                            <TableCell>{fundClass.Currency}</TableCell>
                            <TableCell>
                              {fundClass.IsDefault ? (
                                <Chip label="Yes" size="small" color="primary" />
                              ) : (
                                <Chip label="No" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {fundClass.Timestamp ? new Date(fundClass.Timestamp).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {fundClass.UserName || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Edit Class">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleClassEdit(fundClass)}
                                    disabled={loading}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Class">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setClassToDelete(fundClass);
                                      setDeleteClassDialogOpen(true);
                                    }}
                                    disabled={loading}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={filteredFundClasses.length}
                rowsPerPage={classRowsPerPage}
                page={classPage}
                onPageChange={(event, newPage) => setClassPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setClassRowsPerPage(parseInt(event.target.value, 10));
                  setClassPage(0);
                }}
              />
            </Paper>
          </Grid>
        </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the fund "{fundToDelete?.FundName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fund Class Delete Confirmation Dialog */}
      <Dialog open={deleteClassDialogOpen} onClose={() => setDeleteClassDialogOpen(false)}>
        <DialogTitle>Confirm Delete Fund Class</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the fund class "{classToDelete?.ClassName}" 
            for fund "{funds.find(f => f.BackstopID === classToDelete?.BackstopID)?.FundName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteClassDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleClassDelete} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundManagement;
